import * as angular from 'angular';
import { Map } from 'immutable';
import { assign } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';

import { ChartActions, ChartSwitchActions } from '@mode/charts/domain';
import { CollectionMoveToFacade } from '@mode/collections/domain';
import { GithubFacade } from '@mode/github/contract';
import {
  DeprecatedApiService,
  PdfExportResource,
  ReportResource,
  ReportRunResource,
  ReportThemeResource,
  SpaceResource,
  StarResource,
} from '@mode/legacy/data-access-model-js';
import { RouterStateHelpers } from '@mode/legacy/types-app-state';
import { ReportFacade, ReportUtils } from '@mode/reports/domain';
import { SocketService, SocketSubscription } from '@mode/shared/data-access-socket';
import { ReportResource as ReportResourceV2 } from '@mode/shared/data-access-webapp';
import { FeatureFlag, LegacyAssetService } from '@mode/shared/contract-common';
import { FeatureFlagsFacade } from '@mode/shared/contract-common';
import { ClientActionType } from '../actions/client.actions';
import { QueryActionType } from '../actions/query.actions';
import { ReportActionType, ReportUpdateStrategy } from '../actions/report-actions';
import { ReportLayoutActions } from '../actions/report-layout.actions';
import { ReportRunActionType } from '../actions/report-run.actions';
import {
  currentReportTokenSelector,
  reportResourcesByKeySelector,
  routerSelector,
  selectedReportLayoutSelector,
  selectedReportResourceSelector,
  selectedReportSelector,
  selectedReportSpaceSelector,
  selectedReportUserStarSelector,
  selectedTableSelector,
} from '../reducers/selectors';
import { FlamingoService } from './flamingo-service';
import { QueryDataService } from './query-data-service';
import { ReportSaveService } from './report-save-service';
import ViewService from './view-service';
import { getInitialFlagValue } from '@mode/shared/util-js';

type ReportStrategyPromise = angular.IPromise<{ report: ReportResource; strategy: ReportUpdateStrategy }>;
type ReportStrategyDeferred = angular.IDeferred<{ report: ReportResource; strategy: ReportUpdateStrategy }>;
interface UpdateOptions {
  newChart: boolean;
  strategy: ReportUpdateStrategy;
}

export class ReportDataService {
  static $inject = [
    '$rootScope',
    '$q',
    '$http',
    'RxStoreService',
    'actions$',
    'ReportSaveService',
    'QueryDataService',
    'ViewService',
    'ToastService',
    'ReportModel',
    'QueryModel',
    'ModeResource',
    'QueryTableModel',
    'ChartModel',
    'ApiService',
    'AssetService',
    'ReportFacade',
    'GithubFacade',
    'CollectionMoveToFacade',
    'SocketService',
    'FeatureFlagsFacade',
    'FlamingoService',
  ];

  private _currentReportToken: string;
  private fullReportBuilderRefreshDisabled = true;
  private report$: Observable<ReportResource>;
  private reportSavedSocketSubscription: SocketSubscription<string>;
  private reportSavedEventsSubscription: Subscription;
  private isExportDatasetsEnabled = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

  constructor(
    private $rootScope: angular.IRootScopeService,
    private $q: angular.IQService,
    private $http: angular.IHttpService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private reportSaveService: ReportSaveService,
    private queryDataService: QueryDataService,
    private viewService: ViewService,
    private AlertService,
    private ReportModel,
    private QueryModel,
    private ModeResourceClass,
    private QueryTableModel,
    private ChartModel,
    private api: DeprecatedApiService,
    private assetService: LegacyAssetService,
    private reportFacade: ReportFacade,
    private githubFacade: GithubFacade,
    private collectionMoveToFacade: CollectionMoveToFacade,
    private socketService: SocketService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private _: FlamingoService // Need to load flamingo service because it listens to actions fired by this service
  ) {
    this.report$ = this.getHeaderReportData();

    this.store.select(currentReportTokenSelector).subscribe((currentReportToken) => {
      this._currentReportToken = currentReportToken;
    });

    //-> Load Report from ReportRun
    this.actions$
      .pipe(
        ofType(ReportActionType.LoadEmbeddedReport),
        switchMap((action) =>
          this.api
            .getResource<ReportResource>(action.resource, 'report')
            .then((report) =>
              this.store.dispatch({
                type: ClientActionType.LoadReportSuccess,
                reportResource: report,
                isNew: false,
                isEmbedded: true,
              })
            )
            .catch((err) => this.store.dispatch({ type: ClientActionType.LoadReportFailure, error: err }))
        )
      )
      .subscribe();

    //-> Update Report from report run
    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateEmbeddedReport),
        switchMap((action) => {
          // no need to do an update in this side effect
          const strategy = ReportUpdateStrategy.Silent;
          return this.api
            .getResource<ReportResource>(action.resource, 'report')
            .then((report) =>
              this.store.dispatch({
                type: ReportActionType.UpdateReportSuccess,
                payload: { report, strategy },
                isNew: action.isNew,
              })
            )
            .catch((error) =>
              this.store.dispatch({ type: ReportActionType.UpdateReportFailure, payload: { error, strategy } })
            );
        })
      )
      .subscribe();

    //-> Load PDF Export from ReportRun
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportRunEmbeddedPdfExport),
        switchMap((action) =>
          this.api
            .getResource<PdfExportResource>(action.reportRunResource, 'pdf_export')
            .then((pdfExport) =>
              this.store.dispatch({
                type: ClientActionType.LoadReportRunEmbeddedPdfExportSuccess,
                pdfExportResource: pdfExport,
              })
            )
            .catch((err) =>
              this.store.dispatch({ type: ClientActionType.LoadReportRunEmbeddedPdfExportFailure, error: err })
            )
        )
      )
      .subscribe();

    //-> Load Report from URL
    this.actions$.pipe(ofType(ClientActionType.LoadReportSuccess)).subscribe((action) => {
      if (action.isNew) {
        // create new query for new report
        /*** TODO: Add this back when we no londer load new queries manually in report_edit_controller and router
          let query: QueryResource = new ModeResourceClass({
            name: `Query 1`,
            limit: true,
            token: generateUUID(),
            raw_query: '',
            data_source_id: null,
            _new_query: true, // This needed so token can be replaced with saved query token on the backend
          created_at: new Date().toString()
          });
          this.store.dispatch({ type: QueryActionType.LoadQueriesSuccess, queries: [query] });
          **/
      } else {
        if (action.reportResource.hasEmbedded(this.isExportDatasetsEnabled ? 'local_datasets' : 'queries')) {
          this.store.dispatch({ type: QueryActionType.LoadEmbeddedQueries, report: action.reportResource });
        }
        if (action.reportResource.hasEmbedded('space')) {
          this.store.dispatch({
            type: ClientActionType.LoadReportEmbeddedSpace,
            reportResource: action.reportResource,
          });
        }
        if (action.reportResource.hasLink('creator')) {
          this.store.dispatch({ type: ClientActionType.LoadReportCreator, reportResource: action.reportResource });
        }
        // Load star link if user has starred report
        if (action.reportResource.hasLink('star')) {
          this.store.dispatch({ type: ClientActionType.LoadReportUserStar, reportResource: action.reportResource });
        }

        if (action.reportResource.hasEmbedded('report_theme')) {
          this.api.getResource<ReportThemeResource>(action.reportResource, 'report_theme').then((res) =>
            this.store.dispatch({
              type: ReportActionType.LoadThemeSuccess,
              name: res.name,
              token: res.token,
              href: res.css_href,
              cssSource: res.css_source,
            })
          );
        } else {
          console.warn('No theme for report found. Falling back to default theme');
          // default to some theme
          this.store.dispatch({
            type: ReportActionType.LoadThemeSuccess,
            name: 'Fallback',
            token: 'fallback_theme',
            href: assetService.embedReportCSS,
            cssSource: undefined,
          });
        }
      }
    });

    //-> Load embedded report from report run execution
    this.actions$.pipe(ofType(ReportRunActionType.ExecuteReportRunSuccess)).subscribe((action) => {
      if (action.reportRunResource.hasEmbedded('report')) {
        this.store.dispatch({
          type: ReportActionType.UpdateEmbeddedReport,
          resource: action.reportRunResource,
          isNew: action.isNew,
        });
      }
    });

    //-> Save Report Charts
    this.actions$
      .pipe(
        ofType(ReportActionType.SaveReportCharts),
        map((action) => action.payload),
        switchMap((payload) =>
          this.updateReportCharts(payload.needsUpdate, payload.newChart)
            .then((payload) => this.store.dispatch({ type: ReportActionType.SaveReportChartsSuccess, payload }))
            .catch((payload) => this.store.dispatch({ type: ReportActionType.SaveReportChartsFailure, payload }))
        )
      )
      .subscribe();

    //-> Save Report Tables
    this.actions$
      .pipe(
        ofType(ReportActionType.SaveReportTables),
        map((action) => action.payload),
        switchMap((payload) =>
          this.updateReportTables(payload.needsUpdate)
            .then((payload) => this.store.dispatch({ type: ReportActionType.SaveReportTablesSuccess, payload }))
            .catch((payload) => this.store.dispatch({ type: ReportActionType.SaveReportTablesFailure, payload }))
        )
      )
      .subscribe();

    //-> Update Selected Report Space
    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateSelectedReportSpace),
        map((action) => action.payload),
        withLatestFrom(this.store.select(selectedReportResourceSelector)),
        switchMap(([payload, report]) => {
          const reportParams = {
            ...this.resolveReportModel(report).toParams(),
            'report.space_token': payload.space.token,
            includes: ['space'],
          };

          return report
            .forms('edit')
            .$submit(reportParams)
            .then((response) => {
              this.reportFacade.updateHeaderReportDataManually(response);
              const { isViaPublishAction, switchViewToken } = payload.space;
              this.api.getResource<SpaceResource>(response, 'space').then((space) => {
                this.store.dispatch({
                  type: ReportActionType.UpdateSelectedReportSpaceSuccess,
                  payload: {
                    space: space,
                    report: response,
                    isViaPublishAction,
                    switchViewToken,
                  },
                });
              });
            })
            .catch((err) => {
              this.store.dispatch({
                type: ReportActionType.UpdateSelectedReportSpaceFailure,
                payload: { error: { name: 'Error', message: err.message } },
              });
            });
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateSelectedReportSpaceSuccess),
        map((action) => action.payload),
        filter((payload) => payload?.isViaPublishAction)
      )
      .subscribe((payload) => {
        this.store.dispatch({
          type: ReportActionType.PublishDatasetAfterMoveSpace,
          switchViewToken: payload.switchViewToken,
        });
      });

    // Update Selected Report Space success
    this.actions$.pipe(ofType(ReportActionType.UpdateSelectedReportSpaceSuccess)).subscribe((action) => {
      this.collectionMoveToFacade.dispatch(CollectionMoveToFacade.Actions.moveToCollectionSuccess());
    });

    // Update Selected Report Space failure
    this.actions$.pipe(ofType(ReportActionType.UpdateSelectedReportSpaceFailure)).subscribe((action) => {
      this.collectionMoveToFacade.dispatch(CollectionMoveToFacade.Actions.moveToCollectionFailed());
      console.error(action.payload.error.message);
    });

    //-> Rename Report
    this.actions$
      .pipe(
        ofType(ReportActionType.RenameReport),
        map((action) => action.payload),
        withLatestFrom(
          this.store.select(reportResourcesByKeySelector),
          this.store.select(selectedReportLayoutSelector)
        ),
        map(([payload, reportResources, selectedReportLayout]) => [
          payload,
          this.resolveReportModelFromResourceMap(reportResources, payload.token),
          selectedReportLayout,
        ]),
        switchMap(([payload, reportModel, reportLayout]) => {
          const options = {
            strategy: ReportUpdateStrategy.AlertAndRefreshPreview,
            newChart: false,
          };
          this.reportSaveService.name = payload.name;
          this.reportSaveService.description = payload.description;
          reportModel.name = payload.name;
          reportModel.description = payload.description;
          return this.updateReport(reportModel, options, reportLayout)
            .then((payload) => this.store.dispatch({ type: ReportActionType.UpdateReportSuccess, payload }))
            .catch((payload) => this.store.dispatch({ type: ReportActionType.UpdateReportFailure, payload }));
        })
      )
      .subscribe();

    //-> Update Report-Level Chart Coloring Map
    // Report level coloring isn't used currently, so don't enable this side-effect for now
    // Also, dispatching UpdateReportSuccess has other effects, so maybe a new one should be made for this case as well
    // this.actions$.pipe(ofType(ChartDesignerActionType.UpdateEncoding, ChartDesignerActionType.UpdateColors)).pipe(
    //   withLatestFrom(
    //     this.store.select(selectedReportResourceSelector),
    //     this.store.select(selectedReportLayoutSelector)
    //   ),
    //   map(([action, reportResource, selectedReportLayout])=> [action, this.resolveReportModel(reportResource), selectedReportLayout]),
    //   switchMap(([action, reportModel, reportLayout]) => {
    //     let options = {
    //       strategy: ReportUpdateStrategy.Silent,
    //       newChart: false,
    //     };
    //     this.reportSaveService.report.color_mappings = reportModel.color_mappings;
    //     return this.updateReport(reportModel, options, reportLayout)
    //       .then((payload) => this.store.dispatch({ type: ReportActionType.UpdateReportSuccess, payload }))
    //       .catch((payload) => this.store.dispatch({ type: ReportActionType.UpdateReportFailure, payload }));
    //   }))
    //   .subscribe();

    this.actions$
      .pipe(
        ofType(ReportActionType.SetSelectedReportShared),
        map((action) => action.payload),
        withLatestFrom(this.report$),
        switchMap(([payload, reportData]) =>
          this.updateReportSharedState(reportData, payload.value)
            .then((reportResource) => {
              this.reportFacade.updateHeaderReportSharedState(reportResource.shared, reportResource._forms);
              this.store.dispatch({
                type: ReportActionType.SetSelectedReportSharedSuccess,
                payload: { reportResource },
              });
            })
            .catch((error) => {
              this.store.dispatch({ type: ReportActionType.SetSelectedReportSharedFailure, payload: { error } });
              this.AlertService.error('Something went wrong. Please try disabling external sharing again.');
            })
        )
      )
      .subscribe();

    //-> Update Report
    //   on generic UpdateReport action
    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateReport),
        map((action) => action.payload),
        withLatestFrom(
          this.store.select(reportResourcesByKeySelector),
          this.store.select(selectedReportLayoutSelector)
        ),
        map(([payload, reportResources, selectedReportLayout]) => [
          payload,
          this.resolveReportModelFromResourceMap(reportResources, payload.token),
          selectedReportLayout,
        ]),
        switchMap(([{ strategy, commitMessage }, reportModel, reportLayout]) => {
          return this.updateReport(reportModel, { strategy, newChart: false }, reportLayout)
            .then((success) => {
              this.store.dispatch({
                type: ReportActionType.UpdateReportSuccess,
                payload: { ...success, commitMessage },
              });
            })
            .catch((error) => this.store.dispatch({ type: ReportActionType.UpdateReportFailure, payload: error }));
        })
      )
      .subscribe();
    //   when a chart is added
    this.actions$
      .pipe(
        ofType(ChartActions.createChartSuccess),
        withLatestFrom(
          this.store.select(reportResourcesByKeySelector),
          this.store.select(selectedReportLayoutSelector)
        ),
        map(([action, reportResources, selectedReportLayout]) => [
          this.resolveReportModelFromResourceMap(reportResources, this.getCurrentReportToken()),
          selectedReportLayout,
        ]),
        switchMap(([reportModel, reportLayout]) => {
          const options = { strategy: ReportUpdateStrategy.SilentAndSelectNewV2Chart, newChart: true };
          return this.updateReport(reportModel, options, reportLayout)
            .then((payload) => this.store.dispatch({ type: ReportActionType.UpdateReportSuccess, payload }))
            .catch((payload) => this.store.dispatch({ type: ReportActionType.UpdateReportFailure, payload }));
        })
      )
      .subscribe();

    //-> Update Report Success
    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateReportSuccess),
        withLatestFrom(this.store.select(selectedReportSpaceSelector))
      )
      .subscribe(([action, space]) => {
        switch (action.payload.strategy) {
          case ReportUpdateStrategy.Silent:
          // fallthrough
          case ReportUpdateStrategy.SilentAndSelectNewV2Chart:
            this.reportSaveService.updateReport(action.payload.report);
            break;
          case ReportUpdateStrategy.SilentAndRefreshPreview:
            this.reportSaveService.updateReport(action.payload.report);
            this.attemptPreviewUpdate();
            break;
          case ReportUpdateStrategy.AlertAndRefreshPreview:
            this.reportSaveService.updateReport(action.payload.report);
            this.attemptPreviewUpdate();
            this.alertSaveSuccess();
            break;
        }
        // Update space too if it has changed
        if (!space || space.token !== action.payload.report.space_token) {
          this.store.dispatch({
            type: ClientActionType.LoadReportEmbeddedSpace,
            reportResource: action.payload.report,
          });
        }

        if (action.payload.report.hasEmbedded('report_theme')) {
          this.api.getResource<ReportThemeResource>(action.payload.report, 'report_theme').then((res) => {
            this.store.dispatch({
              type: ReportActionType.LoadThemeSuccess,
              name: res.name,
              token: res.token,
              href: res.css_href,
              cssSource: res.css_source,
            });
          });
        }

        this.reportFacade.updateHeaderReportDataManually(action.payload.report as unknown as ReportResourceV2);
      });

    //-> Update Report Failure
    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateReportFailure),
        map((action) => action.payload)
      )
      .subscribe(({ error, strategy }) => {
        switch (strategy) {
          case ReportUpdateStrategy.Silent:
            break;
          case ReportUpdateStrategy.SilentAndSelectNewV2Chart:
            break;
          case ReportUpdateStrategy.SilentAndRefreshPreview:
            break;
          case ReportUpdateStrategy.AlertAndRefreshPreview:
            this.alertSaveError();
            break;
        }
      });

    //-> Toggle Report User Star
    this.actions$
      .pipe(
        ofType(ReportActionType.ToggleReportUserStar),
        withLatestFrom(this.store.select(selectedReportUserStarSelector), this.report$),
        switchMap(([action, userStar, report]) => {
          if (userStar && userStar.hasForm('destroy')) {
            return this.destroyUserStar(userStar);
          } else if (report.hasEmbedded('new_star') && report.embedded('new_star').hasForm('create')) {
            return this.createUserStar(report);
          }
          return this.$q
            .reject('No star action available')
            .catch((error) => this.store.dispatch({ type: ReportActionType.ToggleReportUserStarFailure, error }));
        })
      )
      .subscribe();

    //-> Save Single Report Chart Success
    this.actions$
      .pipe(
        ofType(ReportActionType.SaveSingleReportChartSuccess),
        map((action) => action.payload)
      )
      .subscribe(({ chart, needsUpdate, newChart }) => {
        this.afterSavedChart(needsUpdate, newChart, chart);
      });

    //-> Save Single Report Table Success
    this.actions$
      .pipe(
        ofType(ReportActionType.SaveSingleReportTableSuccess),
        map((action) => action.payload)
      )
      .subscribe(({ table, needsUpdate }) => {
        this.afterSavedTable(needsUpdate, table);
      });

    //-> Save Single Report Chart Failure
    this.actions$
      .pipe(
        ofType(ReportActionType.SaveSingleReportChartFailure),
        map((action) => action.payload)
      )
      .subscribe(({ error }) => {
        // @todo consider better logging via bugsnag client
        console.error(error);
      });

    //-> Save Single Report Table Failure
    this.actions$
      .pipe(
        ofType(ReportActionType.SaveSingleReportTableFailure),
        map((action) => action.payload)
      )
      .subscribe(({ error }) => {
        // @todo consider better logging via bugsnag client
        console.error(error);
      });

    // side effect to refresh preview if report layout has been updated
    this.actions$
      .pipe(
        ofType(ReportActionType.UpdateIfReportNeedsUpdate),
        withLatestFrom(
          this.store.select(selectedReportResourceSelector),
          this.store.select(selectedReportLayoutSelector)
        )
      )
      .subscribe(([action, reportResource, reportLayout]) => {
        if (reportResource.layout !== reportLayout) {
          this.updateReportSilentlyAndRefreshPreview();
        }
      });

    // Initialize Pusher for Report Invalidations
    this.actions$
      .pipe(
        ofType(ClientActionType.LoadReportSuccess, ReportActionType.UpdateSelectedReportSpaceSuccess),
        withLatestFrom(
          this.store.select(routerSelector),
          this.githubFacade.canSyncToGithub$,
          this.store.select(currentReportTokenSelector)
        ),
        filter(([action, routerState, syncable, token]) => RouterStateHelpers.isEditor(routerState))
      )
      .subscribe(([action, routerState, syncable, token]) => {
        const channelName = `private-report-${token}`;
        if (syncable) {
          this.reportSavedSocketSubscription = this.socketService.connect(channelName);
          // Pusher side-effects
          this.listenToReportSavedEvents();
        } else {
          this.reportSavedSocketSubscription?.disconnect();
        }
      });

    // Auto saves a report when adding or removing a visualization
    this.actions$
      .pipe(
        ofType(
          ReportLayoutActions.Type.AddViz,
          ReportLayoutActions.Type.RemoveViz,
          ChartSwitchActions.deleteChartFromBrowserSuccess,
          ChartSwitchActions.deleteChartFromTabsSuccess
        )
      )
      .subscribe(() => this.updateReportSilently());

    // Used to keep the report save service in sync with the ng2+ store report name changes.
    this.reportFacade.headerReportData$.subscribe((headerReportData) => {
      if (headerReportData?.name) {
        this.reportSaveService.name = headerReportData.name;
      }
    });
  }

  listenToReportSavedEvents() {
    this.reportSavedEventsSubscription?.unsubscribe();
    this.reportSavedEventsSubscription = this.reportSavedSocketSubscription.eventBus
      .pipe(
        withLatestFrom(this.store.select(currentReportTokenSelector)),
        filter(([e, token]) => e.channel === `private-report-${token}`)
      )
      .subscribe(([e, _token]) => {
        switch (e.name) {
          case ReportUtils.ReportEvents.ReportSaved:
            this.store.dispatch({ type: ReportActionType.UpdateSelectedReportLastSavedAt });
            break;
        }
      });
  }

  getHeaderReportData(): Observable<ReportResource> {
    return this.reportFacade.headerReportData$.pipe(
      filter((data) => !!data),
      map((resource) => new this.ModeResourceClass(resource))
    );
  }

  /**
   *
   * Generate report model from a {@link ReportResource}
   *
   * @param reportResources
   * @param token
   * @returns {Model}
   */
  private resolveReportModelFromResourceMap(reportResources: Map<string, ReportResource>, token: string): any {
    const reportResource = reportResources.get(token);

    return this.resolveReportModel(reportResource);
  }

  private resolveReportModel(report: ReportResource): any {
    return new this.ReportModel.model(report, { ignoreEmbedded: true });
  }

  /**
   *
   * Update ReportModel with the latest state that is synced with
   * the {@link ReportSaveService} report
   *
   * @param reportModel
   * @param opts
   * @param reportLayout
   * @returns {IPromise<{report: ReportResource}>}
   */
  private updateReport(
    reportModel: any,
    { newChart, strategy }: UpdateOptions,
    reportLayout: string
  ): ReportStrategyPromise {
    const deferred: ReportStrategyDeferred = this.$q.defer();
    const needsUpdate = reportModel.layout !== reportLayout; // report builder should update if layout has changed
    const syncedReportModel = this.syncWithReportSaveService(reportModel);
    syncedReportModel.layout = reportLayout;

    this.saveRelatedState(needsUpdate, { newChart });

    // legacy - temporary until we get space saving setup
    syncedReportModel.space_token = '';

    this.ReportModel.update(syncedReportModel)
      .then((report) => deferred.resolve({ report: report._resource, strategy }))
      .catch((error) => deferred.reject({ error, strategy }));

    return deferred.promise;
  }

  /**
   *
   * Legacy logic to keep the active report model in
   * sync with the report model on the {@link ReportSaveService}
   *
   * @todo remove need to sync with report save service
   * @param reportModel
   * @param options
   * @returns {Model}
   */
  private syncWithReportSaveService(reportModel: any): any {
    assign(reportModel, this.reportSaveService.report);
    reportModel.name = this.reportSaveService.name;
    reportModel.description = this.reportSaveService.description;
    return reportModel;
  }

  /**
   *
   * @param needsUpdate
   * @param options
   */
  private saveRelatedState(needsUpdate: boolean, options: { newChart: boolean }) {
    this.saveQueries();
    this.saveCharts(needsUpdate, options.newChart);
    this.saveTables(needsUpdate);
  }

  /**
   *
   * Pass through to {@link QueryDataService}
   */
  private saveQueries(): void {
    this.queryDataService.batchSaveOrUpdateQueryState(this.getCurrentReportToken());
  }

  /**
   *
   * @returns {string}
   */
  private getCurrentReportToken(): string {
    return this._currentReportToken;
  }

  /**
   *
   * @todo consider moving chart model handling to {@link SaveReportChartsSuccess/Failure} handler?
   * @param needsUpdate
   * @param newChart
   * @returns {IPromise<[T1,T2,T3,T4,T5,T6,T7,T8,T9,T10]>}
   */
  private updateReportCharts(needsUpdate: boolean, newChart: boolean) {
    const chartPromises = this.reportSaveService.charts
      .filter((chart) => this.reportSaveService.chartHasChanged(chart))
      .map((chart) =>
        chart.resource
          .forms('edit')
          .$submit({
            'chart.view': JSON.stringify(chart.v1View),
            'chart.color_palette_token': chart.colorPaletteToken,
          })
          .then((res) =>
            this.store.dispatch({
              type: ReportActionType.SaveSingleReportChartSuccess,
              payload: { chart: res, needsUpdate, newChart },
            })
          )
          .catch((error) =>
            this.store.dispatch({ type: ReportActionType.SaveSingleReportChartFailure, payload: { error } })
          )
      );

    return this.$q.all(chartPromises);
  }

  /**
   *
   * @param needsUpdate
   * @returns {IPromise<[T1,T2,T3,T4,T5,T6,T7,T8,T9,T10]>}
   */
  private updateReportTables(needsUpdate: boolean) {
    const tablePromises = this.reportSaveService.tables
      .filter((table) => this.reportSaveService.tableHasChanged(table))
      .map((table) =>
        table.resource
          .forms('edit')
          .$submit({
            'query_table.view': JSON.stringify(table.currentView),
          })
          .then((res) =>
            this.store.dispatch({
              type: ReportActionType.SaveSingleReportTableSuccess,
              payload: { table: res, needsUpdate },
            })
          )
          .catch((error) =>
            this.store.dispatch({ type: ReportActionType.SaveSingleReportTableFailure, payload: { error } })
          )
      );

    return this.$q.all(tablePromises);
  }

  private destroyUserStar(userStar: StarResource): angular.IPromise<any> {
    return userStar
      .forms('destroy')
      .$submit()
      .then(() => {
        this.AlertService.success('Removed from Starred');
        this.store.dispatch({ type: ReportActionType.ToggleReportUserStarSuccess, starResource: null });
      })
      .catch((error) => {
        this.AlertService.error('Failed to remove from Starred');
        this.store.dispatch({ type: ReportActionType.ToggleReportUserStarFailure, error });
      });
  }

  private createUserStar(report: ReportResource): angular.IPromise<any> {
    return report
      .embedded('new_star')
      .forms('create')
      .$submit()
      .then((starResource) => {
        this.AlertService.success('Added to Starred');
        this.store.dispatch({ type: ReportActionType.ToggleReportUserStarSuccess, starResource });
      })
      .catch((error) => {
        this.AlertService.error('Failed to add to Starred');
        this.store.dispatch({ type: ReportActionType.ToggleReportUserStarFailure, error });
      });
  }

  private updateReportSharedState(reportResource: ReportResource, value: boolean) {
    return reportResource.forms(value ? 'enable_external_sharing' : 'disable_external_sharing').$submit();
  }

  private attemptPreviewUpdate() {
    this.$rootScope.$broadcast('preview.update');
  }

  private alertSaveSuccess(): void {
    this.AlertService.success('Changes saved');
  }

  private alertSaveError(): void {
    this.AlertService.error('There was a problem saving');
  }

  private afterSavedChart(needsUpdate, newChart, chart) {
    if (!needsUpdate) {
      this.viewService.emitPreviewUpdate('Chart', chart.token, chart.parent, chart.view);
    }
    // If it's a new chart, the V2 select wouldn't have fired earlier, so need to fire now that the chart has been updated
    if (newChart) {
      this.viewService.selectV2Chart(chart, true);
    }
  }

  private afterSavedTable(needsUpdate, table) {
    if (!needsUpdate) {
      this.viewService.emitPreviewUpdate('Table', table.token, table.parent, table.view);
    }
  }

  // observables

  get selectedReportResourceState$() {
    return this.store.select(selectedReportResourceSelector);
  }
  public get selectedReport$() {
    return this.store.select(selectedReportSelector);
  }
  public get selectedReportLayout$() {
    return this.store.select(selectedReportLayoutSelector);
  }

  public get selectedTable$() {
    return this.store.select(selectedTableSelector);
  }

  // actions

  saveCharts(needsUpdate, newChart) {
    this.store.dispatch({
      type: ReportActionType.SaveReportCharts,
      payload: {
        token: this.getCurrentReportToken(),
        needsUpdate,
        newChart,
      },
    });
  }

  saveTables(needsUpdate) {
    this.store.dispatch({
      type: ReportActionType.SaveReportTables,
      payload: {
        token: this.getCurrentReportToken(),
        needsUpdate,
      },
    });
  }

  toggleReportUserStar() {
    this.store.dispatch({
      type: ReportActionType.ToggleReportUserStar,
    });
  }

  loadEmbeddedReport(resource: ReportRunResource) {
    this.store.dispatch({
      type: ReportActionType.LoadEmbeddedReport,
      resource: resource,
    });
  }

  renameReport(token: string, name: string, description: string) {
    this.store.dispatch({
      type: ReportActionType.RenameReport,
      payload: {
        token,
        name,
        description,
      },
    });
  }

  updateIfReportNeedsUpdate() {
    this.store.dispatch({
      type: ReportActionType.UpdateIfReportNeedsUpdate,
    });
  }

  updateReportSettingsSilently(report: any) {
    this.reportFacade.updateHeaderReportDataManually(report);
    this.store.dispatch({
      type: ReportActionType.UpdateReportSuccess,
      payload: {
        report,
        strategy: ReportUpdateStrategy.Silent,
      },
    });
  }

  updateReportSilently(token?: string) {
    this.store.dispatch({
      type: ReportActionType.UpdateReport,
      payload: {
        token: token || this.getCurrentReportToken(),
        strategy: ReportUpdateStrategy.Silent,
      },
    });
  }

  updateReportSilentlyAndRefreshPreview(token?: string) {
    this.store.dispatch({
      type: ReportActionType.UpdateReport,
      payload: {
        token: token || this.getCurrentReportToken(),
        strategy: ReportUpdateStrategy.SilentAndRefreshPreview,
      },
    });
  }

  updateReportWithAlertAndRefreshPreview(token?: string) {
    this.store.dispatch({
      type: ReportActionType.UpdateReport,
      payload: {
        token: token || this.getCurrentReportToken(),
        strategy: ReportUpdateStrategy.AlertAndRefreshPreview,
      },
    });
  }

  updateSelectedReportSpace(space) {
    this.store.dispatch({
      type: ReportActionType.UpdateSelectedReportSpace,
      payload: {
        space,
      },
    });
  }

  updateSelectedReportSharedState(value: boolean) {
    this.store.dispatch({
      type: ReportActionType.SetSelectedReportShared,
      payload: {
        value,
      },
    });
  }

  updateSelectedReportEmbeddedState(value: boolean) {
    this.store.dispatch({
      type: ReportActionType.SetSelectedReportEmbedded,
      payload: {
        value,
      },
    });
  }
}
